import React from 'react';
import {
  Box,
  Avatar,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Link } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Card from '../UIKit/Card';

const UserRankCard = ({
  user,
  isCurrentUser = false,
  showRank = true,
  isTopThree = false,
  showProgress = false,
  timeFrame = 'all-time',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Функция для получения цвета роли
  const getRoleColor = (roles) => {
    const role = roles.includes('admin') ? 'admin' : roles.includes('teacher') ? 'teacher' : 'student';
    switch (role) {
      case 'admin':
        return 'error';
      case 'teacher':
        return 'primary';
      default:
        return 'success';
    }
  };

  // Функция для перевода названия роли
  const getRoleName = (roles) => {
    const role = roles.includes('admin') ? 'admin' : roles.includes('teacher') ? 'teacher' : 'student';
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'teacher':
        return 'Teacher';
      default:
        return 'Student';
    }
  };

  // Цвета и иконки для топ-3 позиций
  const positionColors = {
    1: {
      bg: '#FFB300',
      icon: <EmojiEventsIcon sx={{ color: 'white' }} />,
    },
    2: {
      bg: '#C0C0C0',
      icon: <WorkspacePremiumIcon sx={{ color: 'white' }} />,
    },
    3: {
      bg: '#CD7F32',
      icon: <WorkspacePremiumIcon sx={{ color: 'white' }} />,
    },
  };

  // Рендер карточки для топ-3
  if (isTopThree && user.rank && user.rank <= 3) {
    return (
      <Card
        clickable
        component={Link}
        to={`/profile/${user._id}`}
        sx={{
          display: 'block',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          p: isMobile ? 2 : 3,
          border: isCurrentUser
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.divider}`,
          textDecoration: 'none',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            p: 1,
            bgcolor: positionColors[user.rank]?.bg,
            borderRadius: '0 0 0 12px',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {positionColors[user.rank]?.icon}
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Avatar
            src={user.userMeta?.avatar}
            sx={{
              width: isMobile ? 60 : 80,
              height: isMobile ? 60 : 80,
              mx: 'auto',
              mb: 1,
              border: isCurrentUser
                ? `2px solid ${theme.palette.primary.main}`
                : 'none',
            }}
          />
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
            {user.userMeta?.fullName}
          </Typography>
          <Chip
            label={getRoleName(user.userMeta?.roles || [])}
            color={getRoleColor(user.userMeta?.roles || [])}
            size="small"
            sx={{ mb: 1 }}
          />
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            color="primary"
            fontWeight="bold"
            gutterBottom
          >
            {user.experience} XP
          </Typography>
          <Box
            display="flex"
            justifyContent="center"
            gap={2}
            mb={1}
            color="text.secondary"
          >
            <Box display="flex" alignItems="center" gap={0.5}>
              <SchoolIcon fontSize="small" />
              <Typography variant="body2">
                {user.completedCourses || 0}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <QuizIcon fontSize="small" />
              <Typography variant="body2">
                {user.completedTests || 0}
              </Typography>
            </Box>
          </Box>
          {/* {renderBadges()} */}
        </Box>
      </Card>
    );
  }

  // Стандартный вид карточки для остальных пользователей
  return (
    <Card
      component={Link}
      to={`/profile/${user._id}`}
      sx={{
        display: 'block',
        position: 'relative',
        overflow: 'hidden',
        p: 2,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        },
        border: isCurrentUser
          ? `1px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {showRank && user.rank && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: theme.palette.grey[600],
            }}
          >
            {user.rank}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          ml: showRank ? 4 : 0,
        }}
      >
        <Avatar
          src={user.userMeta?.avatar || '/uploads/avatars/default.jpg'}
          sx={{
            width: 50,
            height: 50,
            mr: 2,
            border: isCurrentUser
              ? `2px solid ${theme.palette.primary.main}`
              : 'none',
          }}
        />

        <Box flexGrow={1}>
          <Typography variant="h6" fontWeight="medium">
            {user.userMeta?.fullName}
          </Typography>

          <Chip
            label={getRoleName(user.userMeta?.roles || [])}
            color={getRoleColor(user.userMeta?.roles || [])}
            size="small"
            sx={{ mt: 0.5 }}
          />
        </Box>

        <Box>
          <Typography
            variant="h6"
            color="primary"
            fontWeight="bold"
            align="right"
          >
            {user.experience} XP
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              mt: 0.5,
            }}
          >
            <SchoolIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {user.completedCourses || 0}
            </Typography>

            <QuizIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {user.completedTests || 0}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default UserRankCard;
